import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'

import {
  countCallsByClientPhone,
  countLeadEventsByLead,
  countSmsByClientPhone,
  getMangoCallDetail,
  getNextLeadCreatedAt,
  listLeadCallResults,
  listCallsByClientPhone,
  listLeadEventsByLead,
  listSmsByClientPhone,
  normalizePhoneDigits,
} from '@/api/inspection-leads'
import type { CallResult, LeadEvent, LeadSms, MangoCall } from '@/types/inspection-lead'
import {
  Globe, Mail, MailX, Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing, Undo2, Wallet,
} from 'lucide-react'

import { Modal } from '@/components/ui/Modal'
import { callAttemptLabel, callResultLabel } from '@/utils/callResult'
import { dateFnsLocale } from '@/utils/dateLocale'
import { formatPhoneDisplay } from '@/utils/phone'

/**
 * Раздел «Коммуникации» в карточке лида: единая лента звонков (Mango) и
 * исходящих SMS (sms_outbox) по телефону клиента, отсортированная по дате
 * (новые сверху).
 *
 * Окно лида — от создания текущего лида до создания следующего лида того
 * же клиента (или «до сейчас», если этот — самый свежий). Сообщения вне
 * окна принадлежат другим заявкам клиента.
 *
 * Звонок: метаданные из mango_calls, транскрипт и ссылка на запись —
 * лениво при открытии модалки (mango-call-detail). Сводка звонка
 * (mango_calls.summary, одна фраза Carlink AI по расшифровке) второй
 * строкой под звонком и в модалке над записью.
 * SMS: текст уже в списке (короткое сообщение), модалка показывает полный
 * текст, дату/время и факт ошибки доставки (без smstraffic-кода — он
 * технический, в UI не показываем).
 */
const cardCls =
  'rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4'

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

type CommItem =
  | { kind: 'call'; at: string; call: MangoCall }
  | { kind: 'sms'; at: string; sms: LeadSms }
  | { kind: 'event'; at: string; event: LeadEvent }

/** Веб-активность клиента — единый серый «глобус» (открыл лендинг,
 *  заполнил форму, перешёл к оплате, смотрит статус). Финансовые
 *  события выделяются: `Wallet` зелёный — клиент оплатил; `Undo2`
 *  оранжевый — деньги вернулись клиенту. */
function EventMark({ type }: { type: LeadEvent['type'] }) {
  const { t } = useTranslation()
  const label = t(`leads.communications.event.${type}`)
  if (type === 'payment_completed') {
    return (
      <span className="inline-flex shrink-0 text-emerald-400" title={label}>
        <Wallet size={16} aria-hidden />
      </span>
    )
  }
  if (type === 'payment_refunded') {
    return (
      <span className="inline-flex shrink-0 text-[color:var(--stryzh-color-accent-orange)]" title={label}>
        <Undo2 size={16} aria-hidden />
      </span>
    )
  }
  return (
    <span className="inline-flex shrink-0 text-[color:var(--stryzh-color-text-secondary)]" title={label}>
      <Globe size={16} aria-hidden />
    </span>
  )
}

function CallMark({ direction, missed }: { direction: MangoCall['direction']; missed?: boolean }) {
  const { t } = useTranslation()
  if (missed) {
    const title =
      direction === 'incoming'
        ? t('leads.calls.missedIncoming')
        : t('leads.calls.missedOutgoing')
    return (
      <span className="inline-flex shrink-0 text-red-400" title={title}>
        <PhoneMissed size={16} aria-hidden />
      </span>
    )
  }
  if (direction === 'incoming') {
    return (
      <span className="inline-flex shrink-0 text-green-400" title={t('leads.calls.incoming')}>
        <PhoneIncoming size={16} aria-hidden />
      </span>
    )
  }
  if (direction === 'outgoing') {
    return (
      <span className="inline-flex shrink-0 text-blue-300" title={t('leads.calls.outgoing')}>
        <PhoneOutgoing size={16} aria-hidden />
      </span>
    )
  }
  return (
    <span className="inline-flex shrink-0 text-[color:var(--stryzh-color-text-secondary)]">
      <Phone size={16} aria-hidden />
    </span>
  )
}

function SmsMark({ status }: { status: LeadSms['status'] }) {
  const { t } = useTranslation()
  if (status === 'failed') {
    return (
      <span className="inline-flex shrink-0 text-red-400" title={t('leads.communications.smsFailed')}>
        <MailX size={16} aria-hidden />
      </span>
    )
  }
  if (status === 'skipped') {
    return (
      <span className="inline-flex shrink-0 text-[color:var(--stryzh-color-text-secondary)]" title={t('leads.communications.smsSkipped')}>
        <MailX size={16} aria-hidden />
      </span>
    )
  }
  return (
    <span className="inline-flex shrink-0 text-purple-300" title={t('leads.communications.smsOut')}>
      <Mail size={16} aria-hidden />
    </span>
  )
}

export function LeadCommunicationsSection({
  leadId,
  clientPhone,
  leadCreatedAt,
}: {
  /** id лида. Если передан — в ленту добавятся клиентские события из
   *  public.lead_events (открыл/отправил форму, перешёл к оплате, оплатил,
   *  посмотрел статус). Без id — только звонки и SMS по телефону. */
  leadId?: string
  clientPhone: string | null
  leadCreatedAt: string
}) {
  const { t } = useTranslation()
  const phone = normalizePhoneDigits(clientPhone)
  const [limit, setLimit] = useState(5)
  const [openCall, setOpenCall] = useState<MangoCall | null>(null)
  const [openSms, setOpenSms] = useState<LeadSms | null>(null)

  const windowQ = useQuery({
    queryKey: ['lead-comm-window', clientPhone, leadCreatedAt],
    queryFn: () =>
      getNextLeadCreatedAt({
        clientPhone: clientPhone as string,
        afterCreatedAt: leadCreatedAt,
      }),
    enabled: !!clientPhone && !!leadCreatedAt,
  })
  const win = { from: leadCreatedAt, to: windowQ.data ?? null }

  // Результат звонка ФО по каждому звонку (последний на звонок): метка в строке.
  const callResultsQ = useQuery({
    queryKey: ['lead-call-results', leadId],
    queryFn: () => listLeadCallResults(leadId as string),
    enabled: !!leadId,
    refetchInterval: 10_000,
  })
  const resultByCall = new Map<string, { result: CallResult; attempt: number | null }>()
  for (const r of callResultsQ.data ?? []) {
    if (r.call_id && !resultByCall.has(r.call_id)) resultByCall.set(r.call_id, r)
  }
  const windowReady = windowQ.isSuccess

  // refetchInterval — чтобы триггерные SMS (paid / report) и звонки появлялись
  // без перезагрузки страницы. 10 секунд — компромисс между «свежо» и «не
  // долбить базу». Ручные SMS отправляются через invalidateQueries из кнопки,
  // поэтому видны мгновенно.
  const callsCountQ = useQuery({
    queryKey: ['mango-calls-count', phone, win.from, win.to],
    queryFn: () => countCallsByClientPhone(phone as string, win),
    enabled: !!phone && windowReady,
    refetchInterval: 10_000,
  })
  // SMS: по телефону клиента в окне лида, плюс всё, что привязано к лиду
  // (SMS дилерам о старте торгов уходят на чужие номера).
  const smsCountQ = useQuery({
    queryKey: ['sms-outbox-count', phone, leadId, win.from, win.to],
    queryFn: () => countSmsByClientPhone(phone, win, leadId ?? null),
    enabled: (!!phone && windowReady) || (!phone && !!leadId),
    refetchInterval: 10_000,
  })
  // Клиентские события (lead_events) — связь жёсткая по lead_id; окно
  // лида не нужно. Refetch чаще: «открыл форму» / «оплатил» — события,
  // на которые оператор должен реагировать быстро.
  const eventsCountQ = useQuery({
    queryKey: ['lead-events-count', leadId],
    queryFn: () => countLeadEventsByLead(leadId as string),
    enabled: !!leadId,
    refetchInterval: 10_000,
  })
  const callsCount = callsCountQ.data ?? 0
  const smsCount = smsCountQ.data ?? 0
  const eventsCount = eventsCountQ.data ?? 0
  const totalCount = callsCount + smsCount + eventsCount

  // Тянем по `limit` обоих типов и мерджим. Так гарантируем, что в превью
  // (limit=5) попадут самые свежие коммуникации, даже если они все одного
  // типа. По кнопке «Показать все» limit поднимаем до totalCount.
  const callsQ = useQuery({
    queryKey: ['mango-calls-list', phone, win.from, win.to, limit],
    queryFn: () => listCallsByClientPhone({ phone: phone as string, limit, win }),
    enabled: !!phone && windowReady && callsCount > 0,
  })
  const smsQ = useQuery({
    queryKey: ['sms-outbox-list', phone, leadId, win.from, win.to, limit],
    queryFn: () => listSmsByClientPhone({ phone, leadId: leadId ?? null, limit, win }),
    enabled: ((!!phone && windowReady) || (!phone && !!leadId)) && smsCount > 0,
  })
  const eventsQ = useQuery({
    queryKey: ['lead-events-list', leadId, limit],
    queryFn: () => listLeadEventsByLead({ leadId: leadId as string, limit }),
    enabled: !!leadId && eventsCount > 0,
  })

  // Раздел рендерим, если хотя бы один источник доступен. Без phone и
  // без leadId смысла нет — пустая шапка.
  if (!phone && !leadId) return null

  const items: CommItem[] = [
    ...(callsQ.data ?? []).map((c) => ({ kind: 'call' as const, at: c.started_at, call: c })),
    ...(smsQ.data ?? []).map((s) => ({ kind: 'sms' as const, at: s.sent_at, sms: s })),
    ...(eventsQ.data ?? []).map((e) => ({ kind: 'event' as const, at: e.at, event: e })),
  ]
    .sort((a, b) => (a.at < b.at ? 1 : -1))
    .slice(0, limit)

  return (
    <section className={cardCls}>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="stryzh-heading-card">{t('leads.communications.title')}</h2>
        {totalCount > 0 ? (
          <span className="rounded-[100px] border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-[length:var(--stryzh-font-size-xs)] font-medium text-cyan-300">
            {totalCount}
          </span>
        ) : null}
      </div>

      {totalCount === 0 ? (
        <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.communications.none')}
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {items.map((it) => {
            if (it.kind === 'call') {
              // Недозвон (answer=0 → duration_sec=0): нет записи/транскрипции,
              // открывать нечего — некликабельная строка с пометкой.
              const missed = it.call.duration_sec === 0
              const rowInner = (
                <>
                  <span className="tabular-nums text-[color:var(--stryzh-color-text-secondary)]">
                    {format(new Date(it.call.started_at), 'dd.MM HH:mm', { locale: dateFnsLocale() })}
                  </span>
                  <CallMark direction={it.call.direction} missed={missed} />
                  <span className="min-w-0 flex-1 truncate text-[color:var(--stryzh-color-text-primary)]">
                    {it.call.manager_name ?? '—'}
                  </span>
                  {resultByCall.has(it.call.id) ? (() => {
                    const cr = resultByCall.get(it.call.id)!
                    const notReached = cr.result === 'no_answer' || cr.result === 'unreachable'
                    return (
                      <span
                        className={
                          'shrink-0 rounded-full px-2 py-0.5 text-[length:var(--stryzh-font-size-xs)] ' +
                          (notReached
                            ? 'bg-[rgba(247,147,26,0.14)] text-[color:var(--stryzh-color-accent-orange)]'
                            : 'bg-[rgba(0,181,173,0.12)] text-[color:var(--stryzh-color-accent-carlink)]')
                        }
                      >
                        {callResultLabel(cr.result, t)}
                        {cr.attempt != null ? ` · ${callAttemptLabel(cr.attempt, t)}` : ''}
                      </span>
                    )
                  })() : null}
                  {/* У недозвона с результатом бейдж уже говорит «Не дозвонились»:
                      красную пометку не дублируем. Без результата (воронки без
                      маркировки звонков, старые звонки) пометка остаётся. */}
                  {missed && resultByCall.has(it.call.id) ? null : (
                    <span
                      className={
                        missed
                          ? 'shrink-0 text-red-300'
                          : 'tabular-nums text-[color:var(--stryzh-color-text-secondary)]'
                      }
                    >
                      {missed ? t('leads.calls.missed') : fmtDuration(it.call.duration_sec)}
                    </span>
                  )}
                </>
              )
              if (missed) {
                return (
                  <div
                    key={'c-' + it.call.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[length:var(--stryzh-font-size-sm)]"
                  >
                    {rowInner}
                  </div>
                )
              }
              return (
                <button
                  key={'c-' + it.call.id}
                  type="button"
                  onClick={() => setOpenCall(it.call)}
                  className="flex flex-col gap-0.5 rounded-md px-2 py-1.5 text-left text-[length:var(--stryzh-font-size-sm)] hover:bg-[rgba(255,255,255,0.05)]"
                >
                  <span className="flex w-full items-center gap-2">{rowInner}</span>
                  {it.call.summary ? (
                    <span className="line-clamp-2 pl-[4.5rem] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
                      {it.call.summary}
                    </span>
                  ) : null}
                </button>
              )
            }
            if (it.kind === 'sms') {
              // «Кем»: для manual — имя диспетчера; для триггерных — «авто».
              // SMS не клиенту (дилерам о торгах): вместо «авто» чужой номер.
              const actor =
                it.sms.source === 'manual'
                  ? it.sms.actor_name ?? null
                  : it.sms.source === 'auction_start' || it.sms.source === 'auction_test'
                    ? formatPhoneDisplay('+' + it.sms.phone)
                    : t('leads.communications.actor.auto')
              return (
                <button
                  key={'s-' + it.sms.id}
                  type="button"
                  onClick={() => setOpenSms(it.sms)}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[length:var(--stryzh-font-size-sm)] hover:bg-[rgba(255,255,255,0.05)]"
                >
                  <span className="tabular-nums text-[color:var(--stryzh-color-text-secondary)]">
                    {format(new Date(it.sms.sent_at), 'dd.MM HH:mm', { locale: dateFnsLocale() })}
                  </span>
                  <SmsMark status={it.sms.status} />
                  <span className="min-w-0 flex-1 truncate text-[color:var(--stryzh-color-text-primary)]">
                    {it.sms.message}
                  </span>
                  {actor ? (
                    <span className="shrink-0 truncate text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                      · {actor}
                    </span>
                  ) : null}
                </button>
              )
            }
            // event: не интерактивный, открывать нечего — просто запись в ленте.
            // «Кем» показываем только для payment_refunded: actor_name диспетчера
            // (ручной возврат) или «авто» (webhook от Т-Банка). Остальные типы —
            // клиентские, актор не нужен.
            const eventActor =
              it.event.type === 'payment_refunded'
                ? it.event.actor_name ?? t('leads.communications.actor.auto')
                : null
            return (
              <div
                key={'e-' + it.event.id}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[length:var(--stryzh-font-size-sm)]"
              >
                <span className="tabular-nums text-[color:var(--stryzh-color-text-secondary)]">
                  {format(new Date(it.event.at), 'dd.MM HH:mm', { locale: dateFnsLocale() })}
                </span>
                <EventMark type={it.event.type} />
                <span className="min-w-0 flex-1 truncate text-[color:var(--stryzh-color-text-primary)]">
                  {t(`leads.communications.event.${it.event.type}`)}
                </span>
                {eventActor ? (
                  <span className="shrink-0 truncate text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                    · {eventActor}
                  </span>
                ) : null}
              </div>
            )
          })}
          {totalCount > items.length ? (
            <button
              type="button"
              onClick={() => setLimit(totalCount)}
              className="mt-1 self-start rounded-md px-2 py-1 text-[length:var(--stryzh-font-size-xs)] text-cyan-300 hover:bg-cyan-400/10"
            >
              {t('leads.communications.showAll', { n: totalCount })}
            </button>
          ) : null}
        </div>
      )}

      {openCall ? <CallDialog call={openCall} onClose={() => setOpenCall(null)} /> : null}
      {openSms ? <SmsDialog sms={openSms} onClose={() => setOpenSms(null)} /> : null}
    </section>
  )
}

function CallDialog({ call, onClose }: { call: MangoCall; onClose: () => void }) {
  const { t } = useTranslation()
  const detailQ = useQuery({
    queryKey: ['mango-call-detail', call.id],
    queryFn: () => getMangoCallDetail(call.id),
  })

  const title = t('leads.calls.dialogTitle', {
    date: format(new Date(call.started_at), 'dd.MM.yyyy HH:mm', { locale: dateFnsLocale() }),
  })

  return (
    <Modal title={title} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          <CallMark direction={call.direction} />
          <span className="text-[color:var(--stryzh-color-text-primary)]">
            {call.manager_name ?? t('leads.calls.managerUnknown')}
          </span>
          <span>· {fmtDuration(call.duration_sec)}</span>
        </div>

        {call.summary ? (
          <div className="rounded-md bg-[rgba(0,181,173,0.08)] px-3 py-2 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]">
            <span className="mb-0.5 block text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-accent-carlink)]">
              {t('leads.calls.summary')}
            </span>
            {call.summary}
          </div>
        ) : null}

        {detailQ.isLoading ? (
          <p className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.calls.loadingDetail')}
          </p>
        ) : detailQ.isError ? (
          <p className="text-[length:var(--stryzh-font-size-sm)] text-red-400">
            {t('leads.calls.detailError')}
          </p>
        ) : (
          <>
            {detailQ.data?.recordingUrl ? (
              <audio
                controls
                preload="none"
                src={detailQ.data.recordingUrl}
                className="w-full"
              >
                {t('leads.calls.audioUnsupported')}
              </audio>
            ) : (
              <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                {t('leads.calls.recordingUnavailable')}
              </p>
            )}

            <TranscriptChat phrases={detailQ.data?.transcript ?? []} />
          </>
        )}
      </div>
    </Modal>
  )
}

function SmsDialog({ sms, onClose }: { sms: LeadSms; onClose: () => void }) {
  const { t } = useTranslation()
  const title = t('leads.communications.smsDialogTitle', {
    date: format(new Date(sms.sent_at), 'dd.MM.yyyy HH:mm', { locale: dateFnsLocale() }),
  })
  // Подпись источника: 'manual' — отправил оператор; 'awaiting_payment' /
  // 'paid' / 'report' — автоматически по триггеру на этом этапе воронки.
  const sourceLabel = t(`leads.communications.smsSource.${sms.source}`)

  return (
    <Modal title={title} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          <SmsMark status={sms.status} />
          <span className="text-[color:var(--stryzh-color-text-primary)]">{sourceLabel}</span>
          {sms.source === 'auction_start' || sms.source === 'auction_test' ? (
            <span className="tabular-nums">· {formatPhoneDisplay('+' + sms.phone)}</span>
          ) : null}
          {sms.status === 'failed' ? (
            <span className="text-red-400">· {t('leads.communications.smsFailed')}</span>
          ) : null}
          {sms.status === 'skipped' ? <span>· {t('leads.communications.smsSkipped')}</span> : null}
        </div>
        <div className="whitespace-pre-wrap rounded-md bg-[rgba(255,255,255,0.05)] px-3 py-2 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]">
          {sms.message}
        </div>
      </div>
    </Modal>
  )
}

function TranscriptChat({
  phrases,
}: {
  phrases: { role: 'agent' | 'client'; text: string }[]
}) {
  const { t } = useTranslation()
  if (phrases.length === 0) {
    return (
      <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
        {t('leads.calls.noTranscript')}
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-1.5">
      {phrases.map((p, i) => (
        <div
          key={i}
          className={
            p.role === 'agent'
              ? 'self-end max-w-[85%] rounded-lg bg-blue-500/15 px-3 py-1.5 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]'
              : 'self-start max-w-[85%] rounded-lg bg-[rgba(255,255,255,0.06)] px-3 py-1.5 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]'
          }
        >
          <span className="mb-0.5 block text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
            {p.role === 'agent' ? t('leads.calls.manager') : t('leads.calls.client')}
          </span>
          {p.text}
        </div>
      ))}
    </div>
  )
}
